import {
    Children,
    isValidElement,
    MouseEvent,
    ReactElement,
    type HTMLAttributes,
    type MouseEventHandler,
    type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import Avatar from '~/components/new/Avatar.tsx';
import Button, {
    ButtonLink,
    type ButtonLinkProps,
    type ButtonProps,
} from '~/components/new/Button.tsx';
import { ChipTag } from '~/components/new/Chip.tsx';
import IconAction, {
    IconActionLink,
    type IconActionLinkProps,
    type IconActionProps,
} from '~/components/new/IconAction.tsx';
import AvatarList from '~/components/ui/AvatarList.tsx';
import { BREAKPOINTS, useBreakpoint } from '~/components/ui/hooks';
import Icon, { RemixIconNames } from '~/components/ui/Icon.tsx';
import MoreOptions from '~/components/ui/MoreOptions';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/list.scss';
import { classNames } from '~/utils/styles.ts';

type ListProps = {
    framed?: boolean;
    padding?: 'big' | 'medium';
    children: ReactElement<typeof Row> | ReactElement<typeof Row>[];
} & Omit<HTMLAttributes<HTMLUListElement>, 'className' | 'children'>;

export default function List({ children, padding = 'big', framed = false, ...props }: ListProps) {
    return (
        <ul
            className={classNames({
                [`list list-padding-${padding}`]: true,
                'list-framed': framed,
            })}
            {...props}>
            {children}
        </ul>
    );
}

// helpers to handle specific behaviors and styles for existing component inside a List.Row
List.AddonChipTag = ChipTag;
List.AddonAvatarList = AvatarList;
List.AddonMoreOptions = MoreOptions;
function AddonMetadata({
    children,
    className = '',
}: {
    children: string | number;
    className?: string;
}) {
    return (
        <span
            className={classNames({
                'content-metadata': true,
                [className]: Boolean(className),
            })}>
            {children}
        </span>
    );
}
List.AddonMetadata = AddonMetadata;

function getClickableAddonProps(onClick?: MouseEventHandler<HTMLElement>) {
    return {
        className: 'flex',
        onClick: (e: MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            onClick?.(e as MouseEvent<HTMLElement>);
            return false;
        },
    };
}

List.AddonButton = function AddonButton({ onClick, ...props }: ButtonProps) {
    return (
        <Button
            {...props}
            {...getClickableAddonProps(onClick)}
        />
    );
};

List.AddonButtonLink = function AddonButtonLink({ onClick, ...props }: ButtonLinkProps) {
    return (
        <ButtonLink
            {...props}
            {...getClickableAddonProps(onClick)}
        />
    );
};

List.AddonIconAction = function AddonIconAction({ onClick, ...props }: IconActionProps) {
    return (
        <IconAction
            {...props}
            {...getClickableAddonProps(onClick)}
        />
    );
};

List.AddonIconActionLink = function AddonIconActionLink({ ...props }: IconActionLinkProps) {
    return <IconActionLink {...props} />;
};

export type ListRowAddon =
    | ReactElement<typeof List.AddonChipTag>
    | ReactElement<typeof List.AddonAvatarList>
    | ReactElement<typeof List.AddonMetadata>
    | ReactElement<typeof List.AddonButton>
    | ReactElement<typeof List.AddonButtonLink>
    | ReactElement<typeof List.AddonIconAction>
    | ReactElement<typeof List.AddonIconActionLink>
    | ReactElement<typeof List.AddonMoreOptions>;

export type ListRowProps = {
    title: ReactNode;
    ellipsis?: boolean;
    subtitle?: ReactNode;
    caption?: ReactNode;
    icon?: ReactElement<typeof Avatar>;
    dot?: boolean;
    children?: ListRowAddon | ListRowAddon[];
    className?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
} & (
    | { to?: string; target?: string; onClick: (e: MouseEvent<HTMLElement>) => void }
    | { to: string; target?: string; onClick?: (e: MouseEvent<HTMLElement>) => void }
    | { to?: string; target?: string; onClick?: (e: MouseEvent<HTMLElement>) => void }
);

function Row({
    title,
    ellipsis = true,
    subtitle,
    caption,
    icon,
    dot = false,
    children = [],
    className = '',
    to,
    target,
    onClick,
    'data-test-component': dataTestComponent = 'new/List/Row',
    'data-test-name': dataTestName,
}: ListRowProps) {
    const navigate = useNavigate();
    const isMobile = useBreakpoint(BREAKPOINTS.sm);

    // hack for backward compatibility of current implementations:
    // extract elements from children when they are of type MoreOptions
    // we need to render moreOptions into a separate container
    const moreOptions = Children.toArray(children).find(
        (child) => isValidElement(child) && child.type === MoreOptions,
    );
    // we need to render metadata into a separate container
    const addonMetadata = Children.toArray(children).find(
        (child) => isValidElement(child) && child.type === AddonMetadata,
    );

    const filteredChildren = Children.toArray(children).filter((child) => {
        // exclude moreOptions (rendered separately) as well as addonMetadata and AvatarList for mobile (hidden on mobile by design)
        if (isValidElement(child) && child.type === MoreOptions) {
            return false;
        }
        if (isValidElement(child) && child.type === AddonMetadata && isMobile) {
            return false;
        }
        if (isValidElement(child) && child.type === AvatarList && isMobile) {
            return false;
        }
        return true;
    });

    return (
        <li
            className={classNames({
                'list-row no-underline': true,
                'list-row-target': Boolean(onClick || to),
                'list-row-dot': dot,
                [className]: Boolean(className),
            })}
            data-test-component={dataTestComponent}
            data-test-name={dataTestName}>
            {/* we're not using Link here because children can contain components that render <a>, nesting them and giving HTML errors */}
            <div
                className={classNames({
                    'list-row-wrapper no-underline': true,
                })}
                data-testid='list-row'
                onClick={(e) => {
                    onClick?.(e);
                    if (to) {
                        const forceBlank = target === '_blank' || e.ctrlKey || e.metaKey;
                        if (to.startsWith('/') && !forceBlank) {
                            void navigate(to);
                        } else {
                            window.open(to, '_blank');
                        }
                    }
                }}>
                {icon && <div className='list-row-icon'>{icon}</div>}
                <div className='list-row-content list-row-content-left'>
                    <div
                        className='list-row-content-title'
                        data-test-name='title'>
                        <Tooltip
                            title={typeof title === 'string' ? title : ''}
                            overflowOnly>
                            {isValidElement(title) ? (
                                title
                            ) : (
                                <span
                                    className={classNames({
                                        ellipsis: ellipsis && !isMobile,
                                    })}>
                                    {title}
                                </span>
                            )}
                        </Tooltip>
                        <span className='list-row-content-title-metadata'>{addonMetadata}</span>
                    </div>
                    {subtitle && (
                        <Tooltip
                            title={typeof subtitle === 'string' ? subtitle : ''}
                            overflowOnly>
                            <div
                                className={classNames({
                                    'list-row-content-subtitle': true,
                                    ellipsis: ellipsis && !isMobile,
                                })}
                                data-test-name='subtitle'>
                                {subtitle}
                            </div>
                        </Tooltip>
                    )}
                    {caption && (
                        <div
                            className={classNames({
                                'list-row-content-caption': true,
                                ellipsis: ellipsis,
                            })}
                            data-test-name='caption'>
                            {caption}
                        </div>
                    )}
                </div>
                <div className='list-row-content-center'>
                    {Children.count(filteredChildren) > 0 && (
                        <div
                            className='list-row-addons'
                            data-test-component='new/List/Row/Addons'>
                            {filteredChildren}
                        </div>
                    )}
                </div>
                <div className='list-row-content-right'>
                    {moreOptions && <div className='list-row-options'>{moreOptions}</div>}
                    {(onClick || to) && (
                        <div
                            className='list-row-caret'
                            data-testid='row-caret'>
                            <Icon
                                icon={RemixIconNames.RiArrowRightSLine}
                                colorVar='core-tertiary'
                            />
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
}

List.Row = Row;
